import React, { useEffect, useState, useCallback } from 'react'
import CONTRACTING from './../../../constants/contracting.constants'
import { observer } from 'mobx-react-lite'
import { MDBCollapse, MDBContainer, MDBRow, MDBCol, MDBIcon, MDBBtn } from 'mdb-react-ui-kit'
import UserProfileService from './../../../shared/services/UserProfile.service'
import UserService from '../../../shared/services/User.service'
import UserContractCartService from './../../../shared/services/UserContractCart.service'
import OnboardingCartList from './../OnboardingCartList/OnboardingCartList.component'
import ElectiveCartList from './../ElectiveCartList/ElectiveCartList.component'
import ReplacementCartList from './../ReplacementCartList/ReplacementCartList.component'
import SaveOnboardingCartButton from './../SaveOnboardingCartButton/SaveOnboardingCartButton.component'
import EnableExternalCarrierToggleCheckbox from './../EnableExternalCarrierToggleCheckbox/EnableExternalCarrierToggleCheckbox.component'
import OnboardingOverrideCart from './../OnboardingOverrideCart/OnboardingOverrideCart.component'
import ACMStore from './../AgentContractManager.store'
import UserContractCartItemFactory from './../../../shared/factories/user-contract-cart-item.factory'
import { toast } from 'react-toastify'

import './AgentOnboardingCart.scss'

const ENABLE_CORE_AUTO_SELECT = CONTRACTING.onboarding.ENABLE_CORE_AUTO_SELECT

const getMaxLimits = () => CONTRACTING.getUsertypeContracting(UserProfileService.isA).max

const isCartClosed = () => ['CLOSED'].includes(ACMStore?.Cart?.get('cart_status'))

const isCartActive = () => ['ACTIVE'].includes(ACMStore?.Cart?.get('cart_status'))

const getRequiredQuantity = () => CONTRACTING.getUsertypeContracting(UserProfileService.isA).min

const setCarriers = spec => {
  // If the cart is active or closed, do not change carriers.
  if (isCartActive() || isCartClosed()) return

  let coreCarriers = [],
    preferredCarriers = [],
    coreLimit = getMaxLimits().core

  try {
    coreCarriers = (ACMStore.Carriers || []).filter(Carrier =>
      Carrier.isPriority(spec, UserProfileService.get('usertype_id'), 'core')
    )
    preferredCarriers = (ACMStore.Carriers || []).filter(Carrier =>
      Carrier.isPriority(spec, UserProfileService.get('usertype_id'), 'preferred')
    )
  } catch (ex) {}

  ACMStore.CartItems = []
  while (coreLimit--) {
    if (Array.isArray(coreCarriers) && coreCarriers.length > 0)
      ACMStore.CartItems.push(
        UserContractCartItemFactory.create({
          cart_id: ACMStore.Cart?.id(),
          item_status: 'DRAFT',
          carrier_id: coreCarriers.shift().id(),
        })
      )
    // CarrierStore.onCarrierToggle(coreCarriers.shift(), true)
    else if (Array.isArray(preferredCarriers) && preferredCarriers.length > 0)
      ACMStore.CartItems.push(
        UserContractCartItemFactory.create({
          cart_id: ACMStore.Cart?.id(),
          item_status: 'DRAFT',
          carrier_id: preferredCarriers.shift().id(),
        })
      )
    // CarrierStore.onCarrierToggle(preferredCarriers.shift(), true)
  }

  return (
    coreCarriers.concat(preferredCarriers).map(Carrier =>
      UserContractCartItemFactory.create({
        cart_id: ACMStore.Cart?.id(),
        item_status: 'DRAFT',
        carrier_id: Carrier.id(),
      })
    ) || []
  )
}

const getIsSelectable = CartItems =>
  !!ACMStore?.Cart && Array.isArray(CartItems) && CartItems.length >= getRequiredQuantity()

// Check if user is BST (internal staff, admin, system admin, or agency owner)
const isBSTUser = () => {
  return UserProfileService.isA(['internal-staff', 'internal-admin', 'system-admin', 'agency-owner'], true)
}

const AgentOnboardingCart = ({ activeSpecs, onCheckout }) => {
  const requiredItems = getRequiredQuantity(),
    [CartItems, setCartItems] = useState([]),
    [ElectiveCartItems, setElectiveCartItems] = useState([]),
    [SelectedCartItems, setSelectedCartItems] = useState([]),
    [uplines, setUplines] = useState([]),
    [isOverrideMode, setIsOverrideMode] = useState(false),
    [hasOverrideCarriers, setHasOverrideCarriers] = useState(false),
    setCarrierListBySpec = useCallback(spec => {
      const ECartItems = setCarriers(spec)
      setCartItems(ACMStore.CartItems)
      setElectiveCartItems(ECartItems)
    }, []),
    isSelectable = getIsSelectable(CartItems)

  const canStartContracting = () => {
    if (hasOverrideCarriers) return true
    return isSelectable && SelectedCartItems.length < getRequiredQuantity()
  }

  // Handle override activation
  const handleOverrideActivation = () => {
    if (
      window.confirm(
        'Are you sure you want to override the carrier selection? This will clear any existing cart items and external carriers.'
      )
    ) {
      setIsOverrideMode(true)

      // Track the override activation
      UserProfileService.track({
        event_type: 'contracting-cart.override.activated',
        event_descrip: 'BST user activated carrier selection override',
        payload: {
          user_id: UserProfileService.getUserId(),
          cart_id: ACMStore.Cart?.id(),
        },
      })

      toast.info('Override mode activated. You can now select carriers manually.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    }
  }

  // Handle override cancellation
  const handleOverrideCancel = () => {
    setIsOverrideMode(false)
  }

  useEffect(() => {
    getUplines()
  }, [])

  const getUplines = async () => {
    const upline_agents = await UserService.getUpline(UserProfileService.getUserId())
    setUplines(upline_agents.map(upline => `${upline.id}`))
  }

  useEffect(() => {
    const loadOverrideCarriers = async () => {
      if (ACMStore.Cart?.id()) {
        const cartId = ACMStore.Cart?.id()
        if (cartId) {
          try {
            const cartData = await UserContractCartService.get(cartId)
            if (cartData && cartData.override_carrier) {
              setHasOverrideCarriers(true)
              const overrideIds = cartData.override_carrier
                .split(',')
                .map(id => parseInt(id))
                .filter(id => !isNaN(id))
              const overrideItems = overrideIds.map(carrierId =>
                UserContractCartItemFactory.create({
                  cart_id: ACMStore.Cart?.id(),
                  item_status: 'DRAFT',
                  carrier_id: carrierId,
                })
              )
              ACMStore.OverrideCartItems = overrideItems
              setCartItems(overrideItems)
              setSelectedCartItems(overrideItems)
            } else {
              // If no override carriers, set default carriers
              setCarrierListBySpec(activeSpecs)
            }
          } catch (error) {
            console.error('Error loading override carriers:', error)
            // Fallback to default carriers on error
            setCarrierListBySpec(activeSpecs)
          }
        } else {
          setCarrierListBySpec(activeSpecs)
        }
      } else {
        setCarrierListBySpec(activeSpecs)
      }
    }
    loadOverrideCarriers()
  }, [setCarrierListBySpec, activeSpecs])

  useEffect(() => {
    if (hasOverrideCarriers) return
    ACMStore.CartItems.forEach(CartItem => {
      const matchingCarrier = (ACMStore?.Carriers || [])
        .filter(Carrier => parseInt(Carrier.id()) === parseInt(CartItem.get('carrier_id')))
        .shift()

      if (!matchingCarrier) ACMStore.remove('cartitem', CartItem)

      if (parseInt(matchingCarrier.get('c_active')) !== 1) ACMStore.remove('cartitem', CartItem)

      if (parseInt(matchingCarrier.get('c_active')) === 1) {
        if (matchingCarrier.get('visibility')?.onboarding?.enable) {
          const usertype_id = parseInt(UserProfileService.getCurrentUserTypeId())
          if (matchingCarrier.get('visibility')?.onboarding?.commission?.enable) {
            let userDetails = UserProfileService.getUserDetails()
            if (matchingCarrier.get('visibility')?.onboarding?.commission?.[userDetails.commission_id]) {
              ACMStore.remove('cartitem', CartItem)
            }
          }
          if ([225].includes(usertype_id) && !matchingCarrier.get('visibility')?.onboarding?.career_agent)
            ACMStore.remove('cartitem', CartItem)
          if ([111, 133].includes(usertype_id) && !matchingCarrier.get('visibility')?.onboarding?.affiliate_agent)
            ACMStore.remove('cartitem', CartItem)
          if ([223, 224].includes(usertype_id) && !matchingCarrier.get('visibility')?.onboarding?.associate_agent)
            ACMStore.remove('cartitem', CartItem)
          if (
            [91, 92, 149, 37, 35, 38, 90, 93, 130, 226, 227, 228, 219, 36, 222].includes(usertype_id) &&
            !matchingCarrier.get('visibility')?.onboarding?.sig_agent
          )
            ACMStore.remove('cartitem', CartItem)
        } else ACMStore.remove('cartitem', CartItem)

        if ((matchingCarrier.get('disabled_uplines') || []).includes(UserProfileService.get('u_upline_id')))
          ACMStore.remove('cartitem', CartItem)
      }

      if (matchingCarrier.get('visibility')?.onboarding?.upline_ids?.length > 0) {
        const disabled_carrier = matchingCarrier
          .get('visibility')
          .onboarding.upline_ids.some(upline_id => uplines.includes(upline_id))
        if (disabled_carrier) ACMStore.remove('cartitem', CartItem)
      }
    })
  }, [setCarrierListBySpec, activeSpecs, uplines])

  const renderWidget = () => {
    if (!ACMStore?.Cart)
      return (
        <MDBContainer fluid id='AgentOnboardingCartComponent'>
          <MDBRow>
            <MDBCol></MDBCol>
          </MDBRow>
        </MDBContainer>
      )

    // If override mode is active, show the override cart
    if (isOverrideMode) {
      return (
        <div id='AgentOnboardingCartComponent'>
          <OnboardingOverrideCart
            activeSpecs={activeSpecs}
            onCheckout={onCheckout}
            onCancel={handleOverrideCancel}
            setHasOverrideCarriers={setHasOverrideCarriers}
            setSelectedCartItems={setSelectedCartItems}
          />
        </div>
      )
    }

    return (
      <div id='AgentOnboardingCartComponent'>
        <div className='section-header'>
          <label className='section-label'>
            <MDBIcon icon='shopping-cart' />
            &nbsp;Agent Onboarding - Carrier Contract Shopping Cart
          </label>

          {/* BST Override Button - Only visible to BST users and during onboarding */}
          {isBSTUser() && (
            <div className='bst-override-controls'>
              <MDBBtn
                type='button'
                color='warning'
                size='sm'
                onClick={handleOverrideActivation}
                className='override-btn'
              >
                <MDBIcon icon='cog' className='mr-2' />
                Override Carrier Selection
              </MDBBtn>
            </div>
          )}
        </div>

        <MDBContainer fluid>
          <MDBRow>
            <MDBCol>
              <OnboardingCartList
                CartItems={CartItems}
                activeSpecs={activeSpecs}
                isSelectable={!ENABLE_CORE_AUTO_SELECT && isSelectable}
                requiredItems={requiredItems}
                SelectedCartItems={SelectedCartItems}
                hasOverrideCarriers={hasOverrideCarriers}
                onSelectCartItem={CartItem => {
                  if (
                    SelectedCartItems.map(CI => parseInt(CI.get('carrier_id'))).indexOf(
                      parseInt(CartItem.get('carrier_id'))
                    ) < 0
                  )
                    setSelectedCartItems(cItems => [...cItems].concat([CartItem]))
                  else {
                    let CartItems = [...SelectedCartItems]
                    setSelectedCartItems(cItems => {
                      let carrierIds = CartItems.map(CI => parseInt(CI.get('carrier_id')))
                      CartItems.splice(carrierIds.indexOf(parseInt(CartItem.get('carrier_id'))), 1)
                      return CartItems
                    })
                  }
                }}
                maxLimit={getMaxLimits().core}
              />
              {!hasOverrideCarriers && (
                <>
                  <MDBCollapse open={ACMStore?.Cart?.id() > 0}>
                    <EnableExternalCarrierToggleCheckbox disabled={!isSelectable} />
                  </MDBCollapse>
                </>
              )}
            </MDBCol>
          </MDBRow>
          {!hasOverrideCarriers && (
            <>
              <MDBRow>
                <MDBCol>
                  <MDBCollapse open={ACMStore?.ExternalCarriers?.length > 0}>
                    <ReplacementCartList
                      CartItems={CartItems}
                      activeSpecs={activeSpecs}
                      ExternalCarriers={ACMStore?.ExternalCarriers}
                    />
                  </MDBCollapse>
                </MDBCol>
              </MDBRow>
              <MDBRow>
                <MDBCol>
                  <ElectiveCartList
                    ElectiveCartItems={ElectiveCartItems}
                    activeSpecs={activeSpecs}
                    isSelectable={isSelectable}
                    onSelectCartItem={carrier => {
                      const addToCart = carrierId => {
                          if (
                            ACMStore.CartItems.map(CartItem => parseInt(CartItem.get('carrier_id'))).indexOf(
                              parseInt(carrierId)
                            ) < 0
                          ) {
                            ACMStore.CartItems.push(
                              UserContractCartItemFactory.create({
                                cart_id: ACMStore.Cart?.id(),
                                item_status: 'DRAFT',
                                carrier_id: carrierId,
                              })
                            )
                          }
                        },
                        removeFromCart = carrierId => {
                          const CartItem = ACMStore.CartItems.filter(
                            CartItem => parseInt(CartItem.get('carrier_id')) === parseInt(carrierId)
                          ).shift()
                          if (CartItem) ACMStore.remove('cartitem', CartItem)
                        },
                        carrierId = Object.keys(carrier || {}).shift()

                      if (carrierId && !isNaN(carrierId)) {
                        if (carrier[carrierId]) addToCart(carrierId)
                        else removeFromCart(carrierId)
                      }
                    }}
                    minLimit={getMaxLimits().electiveMin}
                    maxLimit={getMaxLimits().elective}
                    requiredItems={requiredItems - ACMStore.CartItems.length}
                  />
                </MDBCol>
              </MDBRow>
            </>
          )}
        </MDBContainer>
        <div className='cart-checkout-contents'>
          <MDBCollapse open={ACMStore?.Cart?.id() > 0}>
            <SaveOnboardingCartButton
              disabled={!canStartContracting(SelectedCartItems)}
              SelectedCartItems={SelectedCartItems}
              requiredItems={
                requiredItems - (hasOverrideCarriers ? ACMStore.OverrideCartItems.length : ACMStore.CartItems.length)
              }
              onCheckout={onCheckout}
              hasOverrideCarriers={hasOverrideCarriers}
            />
          </MDBCollapse>
        </div>
      </div>
    )
  }

  return renderWidget()
}

export default observer(AgentOnboardingCart)
